/**
 * Jurisdiction decision for the consent banner (website only).
 *
 * Geo comes from the Netlify edge function netlify/edge-functions/bt-geo.ts, which sets a session
 * cookie `bt_geo` = "<ISO country>" or "<ISO country>-<subdivision>" (e.g. "DE", "CA-QC") from
 * context.geo. The cookie is present before the first page script runs, so the decision is
 * synchronous: no fetch, no waiting. Missing or malformed cookie = unknown location = deny.
 *
 * Global Privacy Control (navigator.globalPrivacyControl === true) in an OPT_OUT region: marketing is
 * DENIED, analytics stays GRANTED. Choice: GPC is a do-not-sell/share signal, and the marketing
 * vendors (ad conversion, Meta, FirstPromoter attribution) are the sharing; first-party analytics
 * stays on, which is the conservative reading that keeps the site measurable. In OPT_IN regions GPC
 * changes nothing (already denied).
 */
import type { ConsentChoices, ConsentLogGeo, ConsentSource, Jurisdiction } from './contract';
import { makeChoices, LOG_COUNTRY_PATTERN, LOG_REGION_PATTERN } from './contract';
import { isOptInLocation } from './jurisdiction-lists';

export const GEO_COOKIE_NAME = 'bt_geo';

export interface JurisdictionDecision {
  jurisdiction: Jurisdiction;
  /** ISO 3166-1 alpha-2, 'XX' when unknown. */
  country: string;
  /** ISO 3166-2 subdivision without country prefix, null when unknown or not provided. */
  region: string | null;
  gpc: boolean;
}

/** Pure: country/region strings from the geo cookie or edge, plus the browser's GPC flag. */
export function decideJurisdiction(input: { country?: string | null; region?: string | null; gpc: boolean }): JurisdictionDecision {
  const country = (input.country ?? '').trim().toUpperCase();
  if (!LOG_COUNTRY_PATTERN.test(country) || country === 'XX') {
    return { jurisdiction: 'unknown_denied', country: 'XX', region: null, gpc: input.gpc };
  }
  let region = (input.region ?? '').trim().toUpperCase();
  if (region.includes('-')) region = region.slice(region.lastIndexOf('-') + 1);
  const regionOrNull = region && LOG_REGION_PATTERN.test(region) ? region : null;
  const optIn = isOptInLocation(country, regionOrNull);
  return {
    jurisdiction: optIn ? 'opt_in' : 'opt_out',
    country,
    region: regionOrNull,
    gpc: input.gpc,
  };
}

/** Parses the bt_geo cookie value. Returns null when absent or malformed. */
export function parseGeoCookie(value: string | null | undefined): { country: string; region: string | null } | null {
  if (!value) return null;
  let decoded: string;
  try {
    decoded = decodeURIComponent(value);
  } catch {
    return null;
  }
  const [country, region] = decoded.split('-', 2);
  if (!country || !LOG_COUNTRY_PATTERN.test(country.toUpperCase()) || country.toUpperCase() === 'XX') return null;
  return { country: country.toUpperCase(), region: region ?? null };
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  try {
    const part = document.cookie
      .split(';')
      .map(p => p.trim())
      .find(p => p.startsWith(name + '='));
    return part ? part.slice(name.length + 1) : null;
  } catch {
    return null;
  }
}

/** True when the browser sends Global Privacy Control (`navigator.globalPrivacyControl`, not in the DOM lib types). */
export function hasGlobalPrivacyControl(): boolean {
  if (typeof navigator === 'undefined' || !('globalPrivacyControl' in navigator)) return false;
  return navigator.globalPrivacyControl === true;
}

/** Reads geo cookie and GPC from the browser. Unknown when the cookie is missing. */
export function currentJurisdiction(): JurisdictionDecision {
  const gpc = hasGlobalPrivacyControl();
  const geo = parseGeoCookie(readCookie(GEO_COOKIE_NAME));
  return decideJurisdiction({ country: geo?.country ?? null, region: geo?.region ?? null, gpc });
}

/**
 * Consent applied to a visitor with no stored choice. OPT_IN and unknown: everything denied (the
 * banner asks). OPT_OUT: analytics granted; marketing granted unless GPC is set.
 */
export function defaultChoicesFor(decision: JurisdictionDecision): { choices: ConsentChoices; source: ConsentSource | null } {
  if (decision.jurisdiction !== 'opt_out') return { choices: makeChoices(false, false), source: null };
  if (decision.gpc) return { choices: makeChoices(true, false), source: 'gpc_opt_out' };
  return { choices: makeChoices(true, true), source: 'default_grant' };
}

/** Geo block for the consent log. */
export function geoForLog(decision: JurisdictionDecision): ConsentLogGeo {
  return {
    jurisdiction: decision.jurisdiction,
    country: decision.country,
    ...(decision.region ? { region: decision.region } : {}),
    gpc: decision.gpc,
  };
}
